const BACKBONE_ENVIRONMENTS = Object.freeze({
    prod: {
        label: 'Production',
        url: process.env.REACT_APP_BACKBONE_URL_PROD,
        apiKey: process.env.REACT_APP_BACKBONE_API_KEY_PROD
    },
    dev: {
        label: 'Development',
        url: process.env.REACT_APP_BACKBONE_URL_DEV,
        apiKey: process.env.REACT_APP_BACKBONE_API_KEY_DEV
    },
    'staging-prod': {
        label: 'Staging Production',
        url: process.env.REACT_APP_BACKBONE_URL_STAGING_PROD,
        apiKey: process.env.REACT_APP_BACKBONE_API_KEY_STAGING_PROD
    },
    'staging-dev': {
        label: 'Staging Development',
        url: process.env.REACT_APP_BACKBONE_URL_STAGING_DEV,
        apiKey: process.env.REACT_APP_BACKBONE_API_KEY_STAGING_DEV
    },
    'maulbeck-prod': {
        label: 'Maulbeck Production',
        url: process.env.REACT_APP_BACKBONE_URL_MAULBECK_PROD,
        apiKey: process.env.REACT_APP_BACKBONE_API_KEY_MAULBECK_PROD
    },
    'maulbeck-dev': {
        label: 'Maulbeck Development',
        url: process.env.REACT_APP_BACKBONE_URL_MAULBECK_DEV,
        apiKey: process.env.REACT_APP_BACKBONE_API_KEY_MAULBECK_DEV
    }
});

const BACKBONE_ENV_STORAGE_KEY = 'backboneEnv';
const FALLBACK_BACKBONE_ENVIRONMENT = 'prod';
let allowNonProductionBackboneEnvironment = false;

const isConfiguredBackboneEnvironment = (environmentConfig) => (
    Boolean(environmentConfig?.url) && Boolean(environmentConfig?.apiKey)
);

const getConfiguredBackboneEnvironmentConfigs = () => Object.fromEntries(
    Object.entries(BACKBONE_ENVIRONMENTS).filter(([, environmentConfig]) => (
        isConfiguredBackboneEnvironment(environmentConfig)
    ))
);

const getConfiguredBackboneEnvironments = () => Object.fromEntries(
    Object.entries(getConfiguredBackboneEnvironmentConfigs()).map(([environment, environmentConfig]) => ([
        environment,
        { label: environmentConfig.label }
    ]))
);

const getFallbackBackboneEnvironment = () => {
    const configuredEnvironments = getConfiguredBackboneEnvironmentConfigs();

    if (configuredEnvironments[FALLBACK_BACKBONE_ENVIRONMENT]) {
        return FALLBACK_BACKBONE_ENVIRONMENT;
    }

    return Object.keys(configuredEnvironments)[0] || FALLBACK_BACKBONE_ENVIRONMENT;
};

const getRestrictedBackboneEnvironment = () => (
    getEnvironmentConfig(FALLBACK_BACKBONE_ENVIRONMENT)
        ? FALLBACK_BACKBONE_ENVIRONMENT
        : getFallbackBackboneEnvironment()
);

const getEnvironmentConfig = (environment) => {
    const environmentConfig = BACKBONE_ENVIRONMENTS[environment];

    return isConfiguredBackboneEnvironment(environmentConfig) ? environmentConfig : null;
};

const isVerboseBackboneEnvironment = (environment) => (
    environment === 'dev' || environment?.endsWith('-dev')
);

// The dev tier is the only non-production one. Pages that warn before writing
// member-visible data share this rule rather than keeping their own env list.
export const isProductionBackboneEnvironment = (environment) => !isVerboseBackboneEnvironment(environment);

const getBackboneStorage = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.sessionStorage;
    } catch (error) {
        return null;
    }
};

const resolveInitialEnvironment = () => {
    const storage = getBackboneStorage();
    let storedEnvironment = null;

    if (storage) {
        try {
            storedEnvironment = storage.getItem(BACKBONE_ENV_STORAGE_KEY);
        } catch (error) {
            storedEnvironment = null;
        }
    }

    return storedEnvironment && getEnvironmentConfig(storedEnvironment)
        ? storedEnvironment
        : getFallbackBackboneEnvironment();
};

let currentEnvironment = resolveInitialEnvironment();

const getEffectiveBackboneEnvironment = () => {
    if (allowNonProductionBackboneEnvironment && getEnvironmentConfig(currentEnvironment)) {
        return currentEnvironment;
    }
    return getRestrictedBackboneEnvironment();
};

export const getBackboneEnvironments = () => getConfiguredBackboneEnvironments();
export const getCurrentBackboneEnvironment = () => getEffectiveBackboneEnvironment();
export const setBackboneEnvironmentAccess = (hasAccess) => {
    allowNonProductionBackboneEnvironment = Boolean(hasAccess);
    return getCurrentBackboneEnvironment();
};
export const reloadBackboneEnvironmentPage = () => {
    window.location.reload();
};
export const setBackboneEnvironment = (environment) => {
    if (!getEnvironmentConfig(environment)) {
        return false;
    }

    if (!allowNonProductionBackboneEnvironment && environment !== getRestrictedBackboneEnvironment()) {
        return false;
    }

    const storage = getBackboneStorage();
    if (!storage) {
        return false;
    }

    try {
        storage.setItem(BACKBONE_ENV_STORAGE_KEY, environment);
    } catch (error) {
        console.error('Failed to persist Backbone environment selection:', error);
        return false;
    }

    currentEnvironment = environment;
    return true;
};

const safeStringify = (value) => {
    try {
        return JSON.stringify(value);
    } catch (error) {
        return '[Unserializable payload]';
    }
};

export const getDataOrRunCommandInBackbone = async (requestBody) => {
    const activeEnvironment = getEffectiveBackboneEnvironment();
    const environmentConfig = getEnvironmentConfig(activeEnvironment);
    const isDebug = isVerboseBackboneEnvironment(activeEnvironment);

    if (!environmentConfig) {
        throw new Error(`Backbone environment "${activeEnvironment}" is not configured.`);
    }

    try {
        if (isDebug) {
            console.log('[Backbone API] Environment:', activeEnvironment);
            console.log('[Backbone API] Request action:', requestBody?.action);
            console.log('[Backbone API] Request payload:', requestBody);
            console.log('[Backbone API] Request payload (JSON):', safeStringify(requestBody));
        }

        const response = await fetch(environmentConfig.url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': environmentConfig.apiKey,
            },
            body: JSON.stringify(requestBody),
        });

        const responseData = await response.json();
        if (isDebug) {
            console.log('[Backbone API] Response:', {
                status: response.status,
                ok: response.ok,
                data: responseData
            });
            console.log('[Backbone API] Response (JSON):', safeStringify(responseData));
        }

        if (!response.ok || responseData.errorCode) {
            console.error('Error in response:', responseData);
            const errorMessage = responseData.errorMessage ||
                responseData.message ||
                responseData.error ||
                `HTTP error! Status: ${response.status} ${response.statusText}`;
            throw new Error(errorMessage);
        }

        return responseData;

    } catch (error) {
        console.error('Error in Backbone API request:', error.message);
        throw error;
    }
};
